import type { ResponsePayload } from '@lk/shared';
import { newAttemptId } from '../lib/ids';
import { getDb, type LearnKidsDb, type QueuedAttempt } from './db';
import { enqueueAttempt } from './queue';
import { getSyncer } from './runtime';

export interface RecordAttemptArgs {
  learnerId: string;
  sessionId: string;
  itemId: string;
  response: ResponsePayload;
  responseMs: number;
  hintsUsed: number;
  db?: LearnKidsDb;
  /** Дозволяє тесту зафіксувати id. У застосунку завжди генерується новий. */
  id?: string;
  now?: number;
}

/**
 * ЄДИНИЙ спосіб зафіксувати відповідь дитини.
 *
 * Порядок дій критичний і саме такий:
 *   1) записати у IndexedDB (durable),
 *   2) повернути керування UI, щоб показати фідбек,
 *   3) і тільки тоді штовхнути синхронізатор.
 *
 * Якщо між (1) і (3) зникне світло — відповідь уже збережена.
 * Зворотний порядок («спершу мережа, потім збереження») — це рівно те,
 * через що стара версія втрачала прогрес.
 */
export async function recordAttempt(args: RecordAttemptArgs): Promise<QueuedAttempt> {
  const db = args.db ?? getDb();
  const now = args.now ?? Date.now();

  const row = await enqueueAttempt({
    db,
    learnerId: args.learnerId,
    now,
    input: {
      id: args.id ?? newAttemptId(),
      sessionId: args.sessionId,
      itemId: args.itemId,
      response: args.response,
      responseMs: Math.max(0, Math.round(args.responseMs)),
      hintsUsed: args.hintsUsed,
      clientTs: new Date(now).toISOString(),
    },
  });

  // Відправка — вже після того, як факт лежить на диску.
  if (!args.db) getSyncer().notifyRecorded();

  return row;
}
